import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  DocumentTextIcon, 
  TrashIcon, 
  EyeIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  CalendarIcon,
  FolderIcon,
  PencilIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

function ViewFile() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Axios Instance
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://api.cdaethi.org/api',
    timeout: 30000,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  const HandleAddfile = () => {
    window.location.href = '/admin/addfile';
  };

  // Fetch files
  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await api.get('/viewfiles');
      const filesData = response.data?.data || response.data || [];
      setFiles(Array.isArray(filesData) ? filesData : []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load files.',
      });
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  // ====================== CORRECT FILE URL ======================
  const getFileUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;

    const filename = path.split(/[/\\]/).pop();
    return `https://api.cdaethi.org/myfiles/${filename}`;
  };

  // Delete file
  const deleteFile = async (id, title) => {
    const confirm = await Swal.fire({
      title: 'Delete File?',
      text: `Are you sure you want to delete "${title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.delete(`/viewfiles/${id}`);
      Swal.fire({ title: 'Deleted!', icon: 'success', timer: 2000 });
      fetchFiles();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to delete file.' });
    }
  };

  // Update file
  const updateFile = async (file) => {
    const { value: formValues } = await Swal.fire({
      title: 'Edit File',
      html: `
        <input id="swal-title" class="swal2-input" placeholder="Title" value="${file.title || ''}">
        <textarea id="swal-description" class="swal2-textarea" placeholder="Description">${file.description || ''}</textarea>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      preConfirm: () => {
        const title = document.getElementById('swal-title').value.trim();
        const description = document.getElementById('swal-description').value.trim();
        if (!title) {
          Swal.showValidationMessage('Title is required');
          return false;
        }
        return { title, description };
      }
    });

    if (formValues) {
      try {
        await api.put(`/viewfiles/${file.id}`, formValues);
        Swal.fire({ title: 'Updated!', icon: 'success', timer: 2000 });
        fetchFiles();
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to update file.' });
      }
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return '📄';
      case 'doc': case 'docx': return '📝';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return '🖼️';
      case 'zip': case 'rar': return '📦';
      default: return '📁';
    }
  };

  const getFileTypeColor = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'bg-red-100 text-red-700';
      case 'doc': case 'docx': return 'bg-blue-100 text-blue-700';
      case 'jpg': case 'jpeg': case 'png': case 'gif': return 'bg-green-100 text-green-700';
      case 'zip': case 'rar': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Filter + Sort + Pagination
  const filteredFiles = files.filter(file => 
    file.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (sortField === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedFiles = sortedFiles.slice(startIndex, startIndex + itemsPerPage);

  const stats = {
    total: files.length,
    recent: files.filter(file => (new Date() - new Date(file.created_at)) / (1000*60*60*24) <= 7).length,
    fileTypes: {
      pdf: files.filter(f => f.file_path?.toLowerCase().endsWith('.pdf')).length,
      documents: files.filter(f => /\.(doc|docx|txt)$/i.test(f.file_path)).length,
      images: files.filter(f => /\.(jpg|jpeg|png|gif)$/i.test(f.file_path)).length,
      archives: files.filter(f => /\.(zip|rar|7z)$/i.test(f.file_path)).length
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUpIcon className="h-4 w-4 opacity-30" />;
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="h-4 w-4" /> : 
      <ChevronDownIcon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 rounded-full animate-spin border-t-indigo-600 mx-auto" />
          <h3 className="mt-6 text-xl font-semibold text-gray-800">Loading Files...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                File Management
              </h1>
              <p className="text-gray-600 mt-2">Manage and organize your documents</p>
            </div>
            <button
              onClick={HandleAddfile}
              className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 flex items-center gap-2 shadow-lg"
            >
              <CloudArrowUpIcon className="h-5 w-5" />
              Upload New File
            </button>
          </div>

          {/* Statistics Cards - You can keep your existing ones */}
        </div>

        {/* Main Content */}
        {paginatedFiles.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <DocumentTextIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium">No files found</p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm ? 'Try adjusting your search' : 'Upload your first file to get started'}
            </p>
            {!searchTerm && (
              <button
                onClick={HandleAddfile}
                className="mt-6 bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700"
              >
                Upload File
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-indigo-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer" 
                        onClick={() => handleSort('title')}>
                      File <SortIcon field="title" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Type</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer" 
                        onClick={() => handleSort('description')}>
                      Description <SortIcon field="description" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer" 
                        onClick={() => handleSort('created_at')}>
                      Uploaded <SortIcon field="created_at" />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedFiles.map((file) => (
                    <tr key={file.id} className="hover:bg-indigo-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">{getFileIcon(file.file_path)}</div>
                          <div>
                            <div className="font-medium text-gray-900">{file.title || 'Untitled'}</div>
                            <div className="text-xs text-gray-500">{file.file_path?.split('/').pop()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getFileTypeColor(file.file_path)}`}>
                          {file.file_path?.split('.').pop()?.toUpperCase() || 'FILE'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {file.description || 'No description'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {file.created_at ? new Date(file.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-3">
                          {file.file_path && (
                            <a
                              href={getFileUrl(file.file_path)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 bg-indigo-100 text-indigo-600 rounded-lg hover:bg-indigo-200 transition flex items-center gap-1"
                              title="Open / View File"
                            >
                              <EyeIcon className="h-5 w-5" />
                              Open
                            </a>
                          )}
                          <button
                            onClick={() => updateFile(file)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                            title="Edit"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => deleteFile(file.id, file.title)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition"
                            title="Delete"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination - Add your pagination code here if needed */}

          </div>
        )}
      </div>
    </div>
  );
}

export default ViewFile;